import { CloseOutlined } from '@ant-design/icons';
import { Button, DatePicker, type DatePickerProps } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Class stamped on the PORTALLED popup root. AntD renders the calendar into
 * `document.body`, which puts it outside `.form-shell` — so the `@container
 * form-shell` queries every other reflow in this product uses cannot reach it,
 * and this class is the only handle `theme/global.css` has on the popup at all.
 * Exported so the test can assert the two stay wired together.
 */
export const DATE_POPUP_CLASS = 'bmo-date-sheet';

type Props = Omit<DatePickerProps, 'classNames' | 'panelRender' | 'open'>;

/**
 * The product's Date control — AntD's `<DatePicker>` plus a phone presentation.
 *
 * On a phone the anchored dropdown does not work: the panel is ~280px wide and
 * ~340px tall against a 375px viewport, so a field anywhere near an edge opens a
 * calendar that runs off the screen with no way to scroll to the rest of it. At
 * `max-width: 767px` the popup therefore becomes a full-screen sheet instead of
 * a dropdown — see the `.bmo-date-sheet` block in theme/global.css. Tablet and
 * desktop keep the ordinary anchored dropdown; it is only clamped so it can
 * never be wider than the viewport.
 *
 * The switch is CSS, not a width read: the chrome below is rendered at every
 * width and `display: contents` removes it above the breakpoint, so the desktop
 * popup is AntD's own markup with no extra boxes in it. That keeps this correct
 * through rotation, split-view and a 200%-zoomed desktop, none of which a
 * `window.innerWidth` snapshot survives.
 *
 * `open` is controlled here for one reason: a full-screen popup covers the
 * whole viewport, so AntD's own "click outside to dismiss" can never fire and
 * the sheet needs a close button that can actually close it. Opening is still
 * entirely AntD's — `onOpenChange` is a faithful relay of its own triggers
 * (focus, click, Escape, outside click, picking a date), so no trigger is lost.
 */
export function BmoDatePicker({ onOpenChange, ...rest }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const setOpenState = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  return (
    <DatePicker
      {...rest}
      open={open}
      onOpenChange={setOpenState}
      // `classNames.popup.root`, not `popupClassName`: the latter is deprecated
      // as of antd 5.25 and logs a console warning on every render.
      classNames={{ popup: { root: DATE_POPUP_CLASS } }}
      panelRender={(panel) => (
        // Both wrappers are `display: contents` above the phone breakpoint, so
        // on desktop this renders exactly as if panelRender were absent.
        <div className="bmo-date-sheet__surface">
          <div className="bmo-date-sheet__bar">
            <span className="bmo-date-sheet__title">{t('formRenderer.datePicker.title')}</span>
            {/* The only way out of a full-screen sheet. A date pick closes it
                too, but "I opened this by mistake" has to be answerable without
                committing a value. */}
            <Button
              type="text"
              icon={<CloseOutlined />}
              aria-label={t('formRenderer.datePicker.close')}
              onClick={() => setOpenState(false)}
            />
          </div>
          <div className="bmo-date-sheet__body">{panel}</div>
        </div>
      )}
    />
  );
}
